import { showConfirmModal } from '@components/ConfirmModal';
import Image from '@components/Image';
import { showInputModal } from '@components/InputModal';
import { PUBLIC_DOMAIN } from '@constants';
import { selectLibSandTable } from '@modules/mediaLib';
import {
  $curSandTableId,
  $curSandTableIdApi,
  $tourDataApi,
} from '@stores/dataStores';
import {
  $sandTableLoadedApi,
  $sandTableMatrixMap,
} from '@stores/sandTableStores';
import {
  DndType,
  type DragItem,
  ImageType,
  type ST,
  type STImage,
  type STMatrix,
  SandTableType,
} from '@types';
import arrayMove from '@utils/arrayMove';
import { getMediaUrl, getThumbUrl } from '@utils/urlMakers';
import classNames from 'classnames';
import { useStore, useStoreMap } from 'effector-react';
import { nanoid } from 'nanoid';
import React, { useMemo, useRef } from 'react';
import { DragPreviewImage, useDrag, useDrop } from 'react-dnd';
import MoreButton from '../../category/MoreButton';
import { deleteSandTable } from '../actions/deleteSandTable';
import fetchSandTableMatrices from '../actions/fetchSandTableMatrices';
import TypeMark from './TypeMark';

interface Props {
  data: ST;
  index: number;
}

const MAP_THUMB = `${PUBLIC_DOMAIN}/imgs/img-map-thumb.343f8b96620120411132656efad02774.png`;
const thumbPreview = (src: string) =>
  `${src}?x-oss-process=image/auto-orient,1/resize,h_40,w_40,m_fill`;
const mediaPreview = (src: string) =>
  `${src}?imageMogr2/auto-orient/thumbnail/40`;

function SandTableItem({ index, data }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const { id, name, type } = data;
  const isActive = useStore($curSandTableId) === id;
  const isMap = type === SandTableType.map;
  const isImage = type === SandTableType.image;
  const isMatrix = type === SandTableType.matrix;

  let mediaUrl: string | undefined;
  let matrixId: number | undefined;
  if (isImage) {
    mediaUrl = data.mediaUrl;
  }
  if (isMatrix) {
    matrixId = data.data;
  }

  const matrixData = useStoreMap({
    store: $sandTableMatrixMap,
    keys: [matrixId],
    fn: (state) => {
      if (matrixId !== undefined) return state.get(matrixId);
    },
  });

  const { src, imageType, previewSrc } = useMemo(() => {
    if (isMap) return { src: MAP_THUMB, previewSrc: thumbPreview(MAP_THUMB) };
    if (isImage) {
      return {
        src: data.mediaUrl,
        imageType: ImageType.media,
        previewSrc: mediaPreview(getMediaUrl(data.mediaUrl)),
      };
    }
    if (isMatrix && matrixData) {
      return {
        src: matrixData.thumb,
        imageType: ImageType.thumb,
        previewSrc: thumbPreview(getThumbUrl(matrixData.thumb)),
      };
    }
    return {};
  }, [type, matrixData, mediaUrl, matrixId]);

  const [{ isDragging }, drag, preview] = useDrag({
    type: DndType.scene,
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: DndType.scene,
    hover(item: DragItem, monitor) {
      if (!ref.current) return;
      const fromIndex = item.index;
      const toIndex = index;
      if (fromIndex === toIndex) return;
      const rect = ref.current.getBoundingClientRect();
      const hoverMiddleX = (rect.right - rect.left) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientX = clientOffset!.x - rect.left;
      if (fromIndex < toIndex && hoverClientX < hoverMiddleX) return;
      if (fromIndex > toIndex && hoverClientX > hoverMiddleX) return;
      handleSort({ fromIndex, toIndex });
      item.index = toIndex;
    },
  });

  drop(drag(ref));

  const handleSort = ({
    fromIndex,
    toIndex,
  }: {
    fromIndex: number;
    toIndex: number;
  }) => {
    $tourDataApi.update((draft) => {
      draft.sandBox = arrayMove({
        array: draft.sandBox,
        fromIndex,
        toIndex,
      });
    });
  };

  const handleRename = async () => {
    const { value } = await showInputModal({
      title: '沙盘重命名',
      placeholder: '请输入沙盘名称',
      initialValue: name,
      maxLength: 50,
    });
    if (value) {
      $tourDataApi.updateSandtable({
        id,
        updater: (draft) => {
          draft.name = value;
        },
      });
    }
  };

  const handleDuplicate = () => {
    $tourDataApi.update((draft) => {
      // 复制并添加在当前沙盘列表的下一个位置
      if (draft.sandBox.length) {
        const id = `s_${nanoid()}`;
        draft.sandBox.splice(index + 1, 0, {
          ...data,
          id,
          name: `${name} 复制`,
        });
      }
    });
  };

  const handleReplace = async () => {
    const { confirmed } = await showConfirmModal({
      title: '温馨提示',
      buttonText: '选择图片',
      hideCancel: true,
      content: (
        <>
          <div className="text-center">
            请尽量选择与原图片沙盘相同尺寸大小及分辨率的图片进行替换以保证替换后已添加的标记点在准确合适的位置
          </div>
          <div className="mt-10 text-center text-sm text-red">
            注意：如果替换图片与原图片差异过大，可能导致已标记点位不可见
          </div>
        </>
      ),
    });
    if (!confirmed) return;
    const { images, matrices } = await selectLibSandTable();
    if (images) {
      const item = images[0];
      // 把loaded置为false, 以触发加载完后的动作
      $sandTableLoadedApi.off();
      $tourDataApi.updateSandtable({
        id,
        updater: (draft) => {
          const draftData = draft as STImage;
          draftData.type = SandTableType.image;
          draftData.mediaId = item.id;
          draftData.mediaUrl = item.url;
        },
      });
    } else if (matrices) {
      const item = matrices[0];
      // 把loaded置为false, 以触发加载完后的动作
      $sandTableLoadedApi.off();
      await fetchSandTableMatrices(item.id);
      $tourDataApi.updateSandtable({
        id,
        updater: (draft) => {
          const draftData = draft as STMatrix;
          draftData.type = SandTableType.matrix;
          draftData.data = item.id;
        },
      });
    }
  };

  const handleDelete = async () => {
    const { confirmed } = await showConfirmModal({
      title: '确认删除沙盘',
      content:
        '沙盘上所有的标记点及使用到该沙盘的其他功能（开场沙盘/场景沙盘）中的该沙盘都将删除，确认删除？',
    });
    if (confirmed) {
      deleteSandTable(id);
    }
  };

  return (
    <div
      ref={ref}
      className={classNames(
        'relative ml-10 h-80 w-80 flex-shrink-0 rounded border-2 border-transparent focus:outline-none',
        {
          'opacity-0': isDragging,
        }
      )}
      onClick={() => isActive || $curSandTableIdApi.set(id)}
    >
      {!!previewSrc && <DragPreviewImage connect={preview} src={previewSrc} />}
      {!!src && (
        <Image
          className={classNames('h-full w-full rounded', {
            'ring-2 ring-orange': isActive,
          })}
          type={imageType}
          src={src}
          size={80}
        />
      )}
      <div
        className="absolute bottom-0 left-0 right-0 truncate rounded-b bg-black-black bg-opacity-50 px-2 text-center text-sm"
        title={name}
      >
        {name}
      </div>
      <TypeMark data={data} />
      <MoreButton
        className="absolute top-4 right-4 rounded bg-black-black bg-opacity-50 hover:bg-blue hover:opacity-100"
        items={
          isMap
            ? (['重新命名', '复制沙盘', '删除沙盘'] as const)
            : (['重新命名', '复制沙盘', '替换图片', '删除沙盘'] as const)
        }
        onSelect={async (val) => {
          if (val === '重新命名') {
            await handleRename();
          } else if (val === '替换图片') {
            await handleReplace();
          } else if (val === '删除沙盘') {
            await handleDelete();
          } else if (val === '复制沙盘') {
            handleDuplicate();
          }
        }}
      />
    </div>
  );
}

export default React.memo(SandTableItem);
